// Occupant service - API supports creating renters with optional user accounts
import { buildApiUrl } from '../config/environment';

export const getOccupants = async (agencyId) => {
  try {
    const token = sessionStorage.getItem('access_token');
    if (!token) {
      throw new Error('Authentication token not found');
    }
    if (!agencyId) {
      throw new Error('Agency ID is required');
    }
    const response = await fetch(buildApiUrl(`/agencies/${agencyId}/renters`), {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch occupants' }));
      throw new Error(error.message || 'Failed to fetch occupants');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching occupants:', error);
    throw error;
  }
};

export const getOccupantById = async (agencyId, id) => {
  try {
    const token = sessionStorage.getItem('access_token');
    if (!token) {
      throw new Error('Authentication token not found');
    }
    if (!agencyId) {
      throw new Error('Agency ID is required');
    }
    const response = await fetch(buildApiUrl(`/agencies/${agencyId}/renters/${id}`), {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch occupant' }));
      throw new Error(error.message || 'Failed to fetch occupant');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching occupant:', error);
    throw error;
  }
};

export const createOccupant = async (agencyId, occupantData) => {
  try {
    const token = sessionStorage.getItem('access_token');
    if (!token) {
      throw new Error('Authentication token not found');
    }
    if (!agencyId) {
      throw new Error('Agency ID is required');
    }

    // Build the payload with the new structure
    // The API now supports create_user field to control whether a user account should be created
    const payload = {
      // Use new field names (preferred according to swagger)
      given: occupantData.given || occupantData.first_name,
      surname: occupantData.surname || occupantData.last_name,
      email: occupantData.email,
      mobile: occupantData.mobile || occupantData.phone || '',
      address: occupantData.address || '',

      // New field to control user creation
      create_user: occupantData.create_user !== undefined ? occupantData.create_user : false,

      // If linking to existing user
      user_id: occupantData.user_id || undefined,

      // Include old field names for backward compatibility if provided
      ...(occupantData.first_name && { first_name: occupantData.first_name }),
      ...(occupantData.last_name && { last_name: occupantData.last_name }),
      ...(occupantData.phone && { phone: occupantData.phone })
    };

    // Remove undefined values to clean up the payload
    Object.keys(payload).forEach(key => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    const response = await fetch(buildApiUrl(`/agencies/${agencyId}/renters`), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      let errorMessage = 'Failed to create occupant';
      try {
        const errorData = await response.json();
        // Handle different error response formats
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.errors && Array.isArray(errorData.errors)) {
          errorMessage = errorData.errors.join(', ');
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (response.status === 400) {
          errorMessage = `Bad Request: ${JSON.stringify(errorData)}`;
        }
      } catch (parseError) {
        // If we can't parse the error response, include the status
        errorMessage = `Failed to create occupant (HTTP ${response.status})`;
      }
      throw new Error(errorMessage);
    }
    return await response.json();
  } catch (error) {
    console.error('Error creating occupant:', error);
    throw error;
  }
};

export const updateOccupant = async (agencyId, id, occupantData) => {
  try {
    const token = sessionStorage.getItem('access_token');
    if (!token) {
      throw new Error('Authentication token not found');
    }
    if (!agencyId) {
      throw new Error('Agency ID is required');
    }

    // Build the payload with the new structure
    const payload = {
      // Use new field names (preferred according to swagger)
      given: occupantData.given || occupantData.first_name,
      surname: occupantData.surname || occupantData.last_name,
      email: occupantData.email,
      mobile: occupantData.mobile || occupantData.phone || '',
      address: occupantData.address || '',

      // If linking to existing user
      user_id: occupantData.user_id || undefined,

      // Include old field names for backward compatibility if provided
      ...(occupantData.first_name && { first_name: occupantData.first_name }),
      ...(occupantData.last_name && { last_name: occupantData.last_name }),
      ...(occupantData.phone && { phone: occupantData.phone })
    };

    // Remove undefined values to clean up the payload
    Object.keys(payload).forEach(key => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    const response = await fetch(buildApiUrl(`/agencies/${agencyId}/renters/${id}`), {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to update occupant' }));
      throw new Error(error.message || 'Failed to update occupant');
    }
    // Handle empty response body gracefully
    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch (error) {
    console.error('Error updating occupant:', error);
    throw error;
  }
};

export const deleteOccupant = async (agencyId, id) => {
  try {
    const token = sessionStorage.getItem('access_token');
    if (!token) {
      throw new Error('Authentication token not found');
    }
    if (!agencyId) {
      throw new Error('Agency ID is required');
    }
    const response = await fetch(buildApiUrl(`/agencies/${agencyId}/renters/${id}`), {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to delete occupant' }));
      throw new Error(error.message || 'Failed to delete occupant');
    }
    return { success: true };
  } catch (error) {
    console.error('Error deleting occupant:', error);
    throw error;
  }
};